import React, { ReactElement } from 'react';
import { Trans } from 'react-i18next';
import i18n from 'translations/i18n';
import TableCellComponent from './table-cell.component';
import { ETableCellEnum, ETableColumns } from './table.enum';
import { TLimitsTableRecord, ColumnsType } from './table.types';
import { ETableRowType } from '../enums/individual-limits.enums';

type TOnCellClickHandler = (record: TLimitsTableRecord, cellId: string[]) => void;

const sharedOnCell = (
  record: TLimitsTableRecord,
  onCellClickHandler: TOnCellClickHandler,
  cellId: ETableColumns[],
): { onClick: () => void } => ({
    ...( record.type === ETableRowType.Operation ? { colSpan: 0 } : null),
    onClick: (): void => {
      onCellClickHandler(record, cellId)
    },
  });

const columns = (
  onCellClickHandler: TOnCellClickHandler,
): ColumnsType<TLimitsTableRecord> => ([
  {
    title: <Trans i18nKey="limits.individualLimits.table.period.column.title.type" />,
    dataIndex: ETableColumns.Type,
    width: 199,
  },
  {
    title: <Trans i18nKey="limits.individualLimits.table.period.column.title.accountNumber" />,
    dataIndex: ETableColumns.AccountNumber,
    width: 199,
  },
  {
    title: <Trans i18nKey="limits.individualLimits.table.period.column.title.currency" />,
    dataIndex: ETableColumns.AsseCode,
    width: 126,
  },
  {
    title: <Trans i18nKey="limits.individualLimits.table.period.column.title.period" />,
    dataIndex: ETableColumns.Period,
    width: 851,
    children: [
      {
        title: <Trans i18nKey="limits.individualLimits.table.period.column.title.day" />,
        dataIndex: [ETableColumns.Period, ETableColumns.Day, ETableColumns.Max],
        width: 157,
        render: (value: string, record: TLimitsTableRecord): ReactElement => {
          let renderCellValue: string | null = value;

          if (record.type === ETableRowType.Operation) {
            const minValue = record?.period?.day?.min;
            const minValuePlaceholder =
              i18n.t('limits.standardLimits.table.noPeriod.column.cell.placeholder.min');
            const maxValue = record?.period?.day?.max;
            const maxValuePlaceholder =
              i18n.t('limits.standardLimits.table.noPeriod.column.cell.placeholder.max');

            renderCellValue = `${minValue || minValuePlaceholder} - ${maxValue || maxValuePlaceholder}`;
          }

          return <TableCellComponent
            type={record.type === ETableRowType.Operation ? ETableCellEnum.MinMax : ETableCellEnum.Max}
          >
            {renderCellValue}
          </TableCellComponent>
        },
        onCell: (record: TLimitsTableRecord) => ({
          onClick: (): void => {
            onCellClickHandler(record, [ETableColumns.Period, ETableColumns.Day]);
          },
          colSpan: record.type === ETableRowType.Operation ? 5 : 1,
        }),
      },
      {
        title: <Trans i18nKey="limits.individualLimits.table.period.column.title.week" />,
        dataIndex: [ETableColumns.Period, ETableColumns.Week, ETableColumns.Max],
        width: 156,
        render: (value: string) => <TableCellComponent type={ETableCellEnum.Max}>
          {value}
        </TableCellComponent>,
        onCell: (record: TLimitsTableRecord) =>
          sharedOnCell(record, onCellClickHandler, [ETableColumns.Period, ETableColumns.Week]),
      },
      {
        title: <Trans i18nKey="limits.individualLimits.table.period.column.title.month" />,
        dataIndex: [ETableColumns.Period, ETableColumns.Month, ETableColumns.Max],
        width: 157,
        render: (value: string) => <TableCellComponent type={ETableCellEnum.Max}>
          {value}
        </TableCellComponent>,
        onCell: (record: TLimitsTableRecord) =>
          sharedOnCell(record, onCellClickHandler, [ETableColumns.Period, ETableColumns.Month]),
      },
      {
        title: <Trans i18nKey="limits.individualLimits.table.period.column.title.year" />,
        dataIndex: [ETableColumns.Period, ETableColumns.Year, ETableColumns.Max],
        width: 158,
        render: (value: string) => <TableCellComponent type={ETableCellEnum.Max}>
          {value}
        </TableCellComponent>,
        onCell: (record: TLimitsTableRecord) =>
          sharedOnCell(record, onCellClickHandler, [ETableColumns.Period, ETableColumns.Year]),
      },
      {
        title: <Trans i18nKey="limits.individualLimits.table.period.column.title.days" />,
        dataIndex: [ETableColumns.Period, ETableColumns.Days, ETableColumns.Max],
        width: 202,
        render: (value: string, record: TLimitsTableRecord) => <TableCellComponent
          type={ETableCellEnum.CustomDays}
        >
          {record?.period?.days?.period_key === null || value === null ? null : (
            <>
              <div>
                {record?.period?.days?.period_key}&nbsp;
                <Trans i18nKey="limits.individualLimits.table.period.column.cell.placeholder.days" />
              </div>
              <div>{value}</div>
            </>
          )}
        </TableCellComponent>,
        onCell: (record: TLimitsTableRecord) =>
          sharedOnCell(record, onCellClickHandler, [ETableColumns.Period, ETableColumns.Days]),
      },
    ],
  }
]);

export default columns;
